import PropTypes from "prop-types";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Virtuoso } from "react-virtuoso";

// Virtualised infinite-scroll wrapper around react-virtuoso.
//
// Replaces the earlier react-window + react-window-infinite-loader +
// react-virtualized-auto-sizer trio. TL;DR: virtuoso's scroll-anchor
// engine handles item-prepend, windowing-trim, and variable item
// heights natively, fixing the scrolling-stuck-at-skeleton bug that
// hit at >1 000 rows under the previous stack.
//
// Public prop + ref API is deliberately unchanged so the four callers
// (ConversationsList, MessagesList, Activities, NotificationsGroup)
// plus BlacklistCard don't have to change. `itemSize` becomes an
// initial height estimate — virtuoso measures actual heights
// automatically.
//
// ## Item-reference stability
//
// The prepend-detection heuristic used by the internal `firstItemIndex`
// tracking relies on REFERENCE equality of `items[items.length - 1]`
// across renders. Callers should avoid rebuilding the items array
// with fresh object references on every render (e.g.
// `items={state.conversations.map(c => ({ ...c }))}`) — doing so
// makes the wrapper interpret every render as a "list replaced"
// event and reset the anchor. All current consumers pass references
// that stay stable across renders (setState-based updates mutate
// only the touched rows). If you're adding a new consumer and see
// scroll position resetting unexpectedly, check for an unnecessary
// `.map(x => ({ ...x }))` in the parent.

// Large enough that prepends can decrement `firstItemIndex` throughout
// any realistic browsing session without going negative.
const FIRST_ITEM_INDEX_BASE = 1_000_000;
// Prefer the item's own `id` for keying and identity comparison so React
// gets a primitive (and doesn't stringify object keys to `[object Object]`,
// which would collide for every row). Falls back to the item itself only
// when no `id` is present — callers in that boat should pass their own
// `getItemKey`.
const identityItemKey = (item) => item?.id ?? item;

const sameItem = (left, right, getItemKey) =>
  getItemKey(left) === getItemKey(right);

// A prepend changes the first item while preserving the last. Appends
// preserve the first item, including lists with stable trailing sentinels.
export const detectPrepend = (prev, items, getItemKey = identityItemKey) => {
  // Null/non-array safety net: callers that hand us something other
  // than an array (mid-initialisation state, reducer typo) get a
  // "no prepend" verdict rather than a runtime crash on .length.
  if (!Array.isArray(prev) || !Array.isArray(items)) {
    return 0;
  }
  if (!prev.length || !items.length) {
    return 0;
  }
  const diff = items.length - prev.length;
  if (diff <= 0) {
    return 0;
  }
  if (!sameItem(items[items.length - 1], prev[prev.length - 1], getItemKey)) {
    return 0;
  }
  // First element identical AND length grew → items were appended,
  // not prepended. Without this guard a stable-tail consumer (e.g.
  // SUBMISSION_ENTRY) would have every next-page load misread as a
  // prepend, dragging firstItemIndex backwards on every fetch.
  if (sameItem(items[0], prev[0], getItemKey)) {
    return 0;
  }
  return diff;
};

// Endpoint changes indicate a filter reset or navigation to a new context.
export const isListReplaced = (prev, items, getItemKey = identityItemKey) => {
  // Mirror of {@link detectPrepend}'s null safety — keeps both
  // exported helpers crash-free for any caller contract violation.
  if (!Array.isArray(prev) || !Array.isArray(items)) {
    return false;
  }
  if (!prev.length || !items.length) {
    return false;
  }
  return (
    !sameItem(prev[0], items[0], getItemKey) &&
    !sameItem(prev[prev.length - 1], items[items.length - 1], getItemKey)
  );
};

const useFirstItemIndex = (items, getItemKey) => {
  const firstItemIndexRef = useRef(FIRST_ITEM_INDEX_BASE);
  const prevItemsRef = useRef(items);
  const [scrollResetCounter, setScrollResetCounter] = useState(0);

  if (prevItemsRef.current !== items) {
    const prev = Array.isArray(prevItemsRef.current)
      ? prevItemsRef.current
      : [];
    const next = Array.isArray(items) ? items : [];
    const prepended = detectPrepend(prev, next, getItemKey);

    if (prepended > 0) {
      firstItemIndexRef.current -= prepended;
      if (firstItemIndexRef.current < 0) {
        firstItemIndexRef.current = FIRST_ITEM_INDEX_BASE;
        setScrollResetCounter((n) => n + 1);
      }
    } else if (next.length === 0 || isListReplaced(prev, next, getItemKey)) {
      // Reset on genuine replacement (filter change, navigation) or
      // an emptied list. An in-place removal (bulk archive) is NOT a
      // replacement — at least one endpoint identity is preserved —
      // so the scroll position stays put rather than jumping to top.
      firstItemIndexRef.current = FIRST_ITEM_INDEX_BASE;
      setScrollResetCounter((n) => n + 1);
    }

    prevItemsRef.current = items;
  }

  return {
    firstItemIndex: firstItemIndexRef.current,
    scrollResetCounter,
  };
};

const InfiniteScroll = forwardRef(
  (
    {
      items,
      renderItem,
      itemSize,
      nextPage,
      prevPage,
      isNextPageLoading,
      isPrevPageLoading = false,
      loadNextPage,
      bottomPadding = 0,
      onItemsRendered = null,
      getItemKey = identityItemKey,
    },
    ref,
  ) => {
    const virtuosoRef = useRef(null);

    const hasNextPage = nextPage != null;
    const hasPrevPage = prevPage != null;
    const isPageLoading = isNextPageLoading;
    const { firstItemIndex, scrollResetCounter } = useFirstItemIndex(
      items,
      getItemKey,
    );

    // Runs once per counter bump. `useLayoutEffect` so the scroll
    // reset happens synchronously with the DOM commit — prevents a
    // visible one-frame flash where the new list renders at the old
    // offset before snapping up. The initial-mount pass (counter===0)
    // is skipped so we don't issue a no-op scroll on first paint.
    useLayoutEffect(() => {
      if (scrollResetCounter === 0) {
        return;
      }
      // Virtuoso's scrollToIndex takes a LOCAL data index (0..length-1),
      // NOT the virtual index — `firstItemIndex` is purely a rendered-
      // ordinal offset for prepend anchoring. Passing the virtual index
      // here would clamp to data.length-1 and scroll to the bottom of
      // the loaded list.
      virtuosoRef.current?.scrollToIndex({ index: 0, align: "start" });
    }, [scrollResetCounter]);

    // Close the one-render gap before `isNextPageLoading` flips to true.
    const inFlightRef = useRef(false);
    useEffect(() => {
      if (!isPageLoading) {
        inFlightRef.current = false;
      }
    }, [isPageLoading]);

    const handleEndReached = useCallback(() => {
      if (inFlightRef.current || !hasNextPage || isPageLoading) {
        return;
      }
      inFlightRef.current = true;
      loadNextPage("next");
    }, [hasNextPage, isPageLoading, loadNextPage]);

    const handleStartReached = useCallback(() => {
      if (inFlightRef.current || !hasPrevPage || isPageLoading) {
        return;
      }
      inFlightRef.current = true;
      loadNextPage("prev");
    }, [hasPrevPage, isPageLoading, loadNextPage]);

    // itemContent signature matches virtuoso's (index, item). We pass
    // the item through to the caller's renderItem with the legacy
    // (item, isLoading, index) contract. Virtuoso never calls this for
    // the skeleton row — that lives in the Footer slot.
    const itemContent = useCallback(
      (index, item) => renderItem(item, false, index),
      [renderItem],
    );

    const handleItemsRendered = useCallback(
      (renderedItems) => {
        onItemsRendered?.(renderedItems);
      },
      [onItemsRendered],
    );

    // Footer carries the skeleton "loading more…" row. Trailing spacer
    // is rendered only when a caller opts in via `bottomPadding > 0`
    // (e.g. to clear a fixed bottom toolbar). Defaulting to 0 — the
    // legacy 300px default left half-empty viewports below the last
    // row for consumers that never had an overlapping toolbar.
    //
    // `role="status"` + `aria-live="polite"` announces each page
    // load to screen readers without interrupting ongoing reading.
    // Wrapping the skeleton in a live region is the virtuoso
    // equivalent of what react-window's `isLoading` row previously
    // provided via ARIA on the row itself.
    const Footer = useMemo(
      () => () => {
        // Don't render the footer skeleton during a prev-direction
        // load — `isPageLoading` is truthy for any in-flight load
        // (so the inFlightRef gate stays correct), but only the
        // Header should render its skeleton on prev. Otherwise the
        // user sees two skeletons during a single round-trip.
        const isLoadingMore =
          hasNextPage && isPageLoading && !isPrevPageLoading;
        return (
          <div
            role={isLoadingMore ? "status" : undefined}
            aria-live={isLoadingMore ? "polite" : undefined}
          >
            {isLoadingMore ? renderItem(undefined, true) : null}
            {bottomPadding > 0 ? (
              <div style={{ height: bottomPadding }} />
            ) : null}
          </div>
        );
      },
      [
        hasNextPage,
        isPageLoading,
        isPrevPageLoading,
        renderItem,
        bottomPadding,
      ],
    );

    // Header mirrors Footer for the prev direction. Renders the
    // skeleton row above the first item while a previous page is in
    // flight, so scroll-up loads have the same affordance scroll-down
    // loads do. Opt-in via `isPrevPageLoading` — consumers that don't
    // pass it (or never paginate prev) get no header DOM.
    const Header = useMemo(
      () => () => {
        const isLoadingPrev = hasPrevPage && isPrevPageLoading;
        if (!isLoadingPrev) {
          return null;
        }
        return (
          <div role="status" aria-live="polite">
            {renderItem(undefined, true)}
          </div>
        );
      },
      [hasPrevPage, isPrevPageLoading, renderItem],
    );

    const components = useMemo(() => ({ Footer, Header }), [Footer, Header]);

    // Preserve the `scrollToItem` part of the legacy imperative API so
    // Activities' scroll-to-highlighted-entry still works. The caller
    // passes a LOCAL data index (0..items.length-1), which is exactly
    // what Virtuoso's imperative `scrollToIndex` expects even when
    // `firstItemIndex` is used for prepend anchoring.
    //
    // The old `forceUpdateList()` method was dropped in the virtuoso
    // migration — virtuoso auto-remeasures on `data` change, so the
    // react-window cache-invalidation hook it wrapped is no longer
    // needed.
    useImperativeHandle(
      ref,
      () => ({
        scrollToItem: (index, align = "auto") => {
          virtuosoRef.current?.scrollToIndex({
            index,
            align,
          });
        },
      }),
      [],
    );

    return (
      <Virtuoso
        ref={virtuosoRef}
        data={items}
        firstItemIndex={firstItemIndex}
        computeItemKey={(_, item) => getItemKey(item)}
        itemContent={itemContent}
        itemsRendered={handleItemsRendered}
        endReached={hasNextPage ? handleEndReached : undefined}
        startReached={hasPrevPage ? handleStartReached : undefined}
        components={components}
        defaultItemHeight={itemSize}
        increaseViewportBy={{ top: 200, bottom: 200 }}
        // `atBottomThreshold: 0` makes virtuoso consider the list "at
        // bottom" only when scrollTop is exactly scrollHeight −
        // clientHeight. The default (4 px) was permissive enough that
        // a loaded chunk shorter than the viewport registered as "at
        // bottom" on initial mount, firing `endReached` once per
        // state change and racing pagination with the deep-link
        // anchor scroll.
        atBottomThreshold={0}
        style={{ height: "100%" }}
      />
    );
  },
);

InfiniteScroll.propTypes = {
  // Array of loaded items. Stable references across renders are
  // expected — see the "Item-reference stability" section at the top
  // of this file.
  items: PropTypes.array.isRequired,
  // Renders one row. Signature: `(item, isLoadingSkeleton, index)`.
  // `isLoadingSkeleton` is true only for the Footer's skeleton row
  // (when `nextPage && isNextPageLoading`); `item` is undefined in
  // that case.
  renderItem: PropTypes.func.isRequired,
  // Initial height estimate in px. Virtuoso auto-measures from the
  // actual rendered DOM; this only affects the briefly-visible
  // pre-measurement state.
  itemSize: PropTypes.number,
  // Truthy when a next page is available. `null` / `undefined` signals
  // end-of-list. Either a page number or cursor string is accepted.
  nextPage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Truthy when a previous page is available (bidirectional consumers
  // only — Activities today).
  prevPage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isNextPageLoading: PropTypes.bool,
  // Truthy while the prev page is being fetched. Drives the Header
  // skeleton row, mirroring how `isNextPageLoading` drives the Footer.
  // Defaults to false; bidirectional consumers should opt in.
  isPrevPageLoading: PropTypes.bool,
  // Called when the user reaches the end (`'next'`) or start
  // (`'prev'`) of the list.
  loadNextPage: PropTypes.func,
  // Trailing empty space below the last row. Opt-in (default 0) so
  // consumers without a fixed bottom toolbar don't inherit phantom
  // whitespace. Pass a positive px value only when the list overlaps
  // a fixed bottom element (composer, toolbar) and the last row
  // needs headroom to scroll above it.
  bottomPadding: PropTypes.number,
  // Called whenever Virtuoso renders a new item range. Consumers can
  // wait for a target row to exist before doing DOM-based alignment.
  onItemsRendered: PropTypes.func,
  // Optional stable key extractor for prepend/replace detection.
  getItemKey: PropTypes.func,
};

export default InfiniteScroll;
